import { boolean, object, string } from "yup";
import { getSharedSchema } from "./schemas/shared.schema";
import {
    getSchemaEE,
    getSchemaES,
    getSchemaCA,
    getSchemaIN,
    getSchemaKR,
    getSchemaMY,
    getSchemaSG,
    getSchemaUS,
    getSchemaAU,
    getSchemaNZ,
    getRegionSchema,
    getSchemaJP,
    getSchemaGB,
    getSchemaPL,
    getSchemaPT,
    getSchemaHU,
    getSchemaHR,
    getSchemaBE,
    getSchemaFR,
    getSchemaSI,
    getSchemaSK,
    getSchemaCZ,
    getSchemaAT,
    getSchemaCH,
    getSchemaDE,
    getSchemaNL,
    getSchemaLV,
    getSchemaLT,
    getSchemaSE,
    getSchemaDK,
    getSchemaFI,
    getSchemaIE,
    getSchemaNO,
    getSchemaCY,
    getSchemaCABillingAddress,
} from "./schemas/specific.schema";
import { ShippingMethodTypes } from "@features/checkout/types";
import { Market, type CountryCode } from "@config/site/types";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import { getFormsConfig } from "@features/checkout/components/delivery-step/FormsConfig";
import { getVatValidation } from "./schemas/vat.schema";
import type { RegionalFieldFlags } from "@lib/utils/checkoutHelper";

// Defaults preserve the legacy "required where states exist" behaviour for callers
// (profile, Apple Pay) that don't pass the checkout feature flags.
const DEFAULT_REGIONAL_FIELD_FLAGS: RegionalFieldFlags = {
    isStateFieldEnabled: true,
    isDistrictFieldEnabled: true,
};

export type DeliveryStepFormErrorHeaders = {
    firstName?: string;
    lastName?: string;
    address?: string;
    requiredField?: string;
    firstNameLabel?: string;
    lastNameLabel?: string;
    companyLabel?: string;
    streetNameLabel?: string;
    cityLabel?: string;
    districtLabel?: string;
    postalCodeLabel?: string;
    regionLabel?: string;
    vatNumberLabel?: string;
    customerNumberLabel?: string;
    salutationLabel?: string;
    phoneticFirstNameLabel?: string;
    phoneticLastNameLabel?: string;
    addressLine2Label?: string;
    landmarkLabel?: string;
    countryLabel?: string;
    zipCodeValid?: string;
    cityNumbers?: string;
    countryExists?: string;
    maxNameCharacters?: string;
    maxCompanyCharacters?: string;
    maxAddressCharacters?: string;
    streetNameMaxLength?: string;
    deliveryMethod?: string;
    poBox?: string;
    phoneticInvalid?: string;
    vatNumber?: string;
    customerNumber?: string;
};

export const getNonDefaultSchema = (
    headers: DeliveryStepFormErrorHeaders,
    countryCode: CountryCode,
    locale: string,
    addressForm?: "deliveryAddress" | "billingAddress",
    forProfile?: boolean,
    flags: RegionalFieldFlags = DEFAULT_REGIONAL_FIELD_FLAGS
) => {
    const regionSchema = getRegionSchema(headers, countryCode, locale, flags.isStateFieldEnabled);
    const country = countryCode.toLowerCase() as Market;

    switch (country) {
        case Market.MY:
            return { ...getSchemaMY(headers), ...regionSchema };
        case Market.IN:
            return { ...getSchemaIN(headers), ...regionSchema };
        case Market.SG:
            return getSchemaSG(headers);
        case Market.ES:
            return getSchemaES(headers, forProfile);
        case Market.EE:
            return getSchemaEE(headers);
        case Market.KR:
            return { ...getSchemaKR(headers), ...regionSchema };
        case Market.CA:
            if (addressForm === "billingAddress") {
                return { ...getSchemaCABillingAddress(headers), ...regionSchema };
            }
            return { ...getSchemaCA(headers), ...regionSchema };
        case Market.US:
            return { ...getSchemaUS(headers), ...regionSchema };
        case Market.AU:
            return { ...getSchemaAU(headers), ...regionSchema };
        case Market.NZ:
            // Do not spread regionSchema: NZ uses district instead of region (FormsConfig).
            return { ...getSchemaNZ(headers, flags.isDistrictFieldEnabled) };
        case Market.JP:
            return { ...getSchemaJP(headers), ...regionSchema };
        case Market.GB:
            return getSchemaGB(headers);
        case Market.PL:
            return getSchemaPL(headers);
        case Market.PT:
            return getSchemaPT(headers);
        case Market.HU:
            return getSchemaHU(headers);
        case Market.HR:
            return getSchemaHR(headers);
        case Market.BE:
            return getSchemaBE(headers);
        case Market.FR:
            return getSchemaFR(headers);
        case Market.SI:
            return getSchemaSI(headers);
        case Market.SK:
            return getSchemaSK(headers);
        case Market.CZ:
            return getSchemaCZ(headers);
        case Market.AT:
            return getSchemaAT(headers);
        case Market.CH:
            return getSchemaCH(headers);
        case Market.DE:
            return getSchemaDE(headers, addressForm, forProfile);
        case Market.NL:
            return getSchemaNL(headers);
        case Market.LV:
            return getSchemaLV(headers);
        case Market.LT:
            return getSchemaLT(headers);
        case Market.SE:
            return getSchemaSE(headers);
        case Market.DK:
            return getSchemaDK(headers);
        case Market.FI:
            return getSchemaFI(headers);
        case Market.IE:
            return getSchemaIE(headers);
        case Market.NO:
            return getSchemaNO(headers);
        case Market.CY:
            return getSchemaCY(headers);
        default:
            // If you get linting error here, it's because you need to add a case for the new country
            // eslint-disable-next-line no-case-declarations
            const exhaustiveCheck: never = country;
            void exhaustiveCheck;
            return {};
    }
};

export default function getDeliveryStepValidationSchema(
    headers: DeliveryStepFormErrorHeaders,
    deliveryCountry: CountryCode,
    billingCountry: CountryCode,
    locale: string,
    flags: RegionalFieldFlags = DEFAULT_REGIONAL_FIELD_FLAGS,
    isLoyaltyMember = false
) {
    const { enableVatNumber } = getFormsConfig(billingCountry);
    const sharedSchema = getSharedSchema(headers, {
        firstName: headers.firstNameLabel,
        lastName: headers.lastNameLabel,
        company: headers.companyLabel,
        streetName: headers.streetNameLabel,
        postalCode: headers.postalCodeLabel,
        city: headers.cityLabel,
        country: headers.countryLabel,
    });

    const isPickUpMethod = (deliveryMethodId: string) =>
        deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT) ||
        deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.PARCEL_SHOP);

    return object().shape({
        ...(enableVatNumber &&
            !isLoyaltyMember && {
                vatNumber: getVatValidation(billingCountry, headers).when(
                    [
                        "hasSameBillingAddress",
                        "deliveryMethodId",
                        "deliveryAddress.isCompany",
                        "billingAddress.isCompany",
                    ],
                    (
                        [
                            hasSameBillingAddress,
                            deliveryMethodId,
                            deliveryIsCompany,
                            billingIsCompany,
                        ],
                        schema
                    ) => {
                        const isCompanyOrder =
                            hasSameBillingAddress && !isPickUpMethod(deliveryMethodId)
                                ? Boolean(deliveryIsCompany)
                                : Boolean(billingIsCompany);

                        return isCompanyOrder
                            ? schema.required(headers.requiredField)
                            : string().notRequired();
                    }
                ),
            }),
        hasSameBillingAddress: boolean(),
        deliveryMethodId: string().required(headers.requiredField),
        deliveryAddress: object().when("deliveryMethodId", ([deliveryMethodId], schema) => {
            return isPickUpMethod(deliveryMethodId)
                ? schema
                : sharedSchema.shape(
                      getNonDefaultSchema(
                          headers,
                          deliveryCountry,
                          locale,
                          undefined,
                          undefined,
                          flags
                      )
                  );
        }),

        billingAddress: object().when(
            "hasSameBillingAddress",
            ([hasSameBillingAddress], schema) => {
                return hasSameBillingAddress === false
                    ? sharedSchema.shape(
                          getNonDefaultSchema(
                              headers,
                              billingCountry,
                              locale,
                              "billingAddress",
                              undefined,
                              flags
                          )
                      )
                    : schema;
            }
        ),
    });
}
